import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSecureAuth } from '../contexts/SecureAuthContext';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, ArrowLeft, Check, X, KeyRound } from 'lucide-react';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { forgotPassword, resetPassword } = useSecureAuth();
  
  const [step, setStep] = useState('email'); // 'email', 'code', 'newPassword', 'success'
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Password validation
  const passwordChecks = {
    length: newPassword.length >= 8,
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword)
  };
  
  const isPasswordValid = Object.values(passwordChecks).every(Boolean);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const result = await forgotPassword(email);
      
      if (result.success) {
        toast.success('Reset code sent to your email');
        if (result.testCode) {
          toast.info(`Test code: ${result.testCode}`, { duration: 15000 });
        }
        setStep('code');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to send reset code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = (e) => {
    e.preventDefault();
    if (code.length === 6) {
      setStep('newPassword');
    } else {
      toast.error('Please enter a 6-digit code');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!isPasswordValid) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }
    
    setLoading(true);
    
    try {
      const result = await resetPassword(email, code, newPassword);
      
      if (result.success) {
        setStep('success');
      } else {
        toast.error(result.error);
      }
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const PasswordCheck = ({ passed, label }) => (
    <div style={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: '0.5rem',
      fontSize: '0.8rem',
      color: passed ? '#16a34a' : '#9ca3af'
    }}>
      {passed ? <Check size={14} /> : <X size={14} />}
      {label}
    </div>
  );

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0073ea 0%, #0052a3 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        background: '#fff',
        borderRadius: '16px',
        padding: '2.5rem',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)'
      }}>
        {/* Success Screen */}
        {step === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: '#dcfce7',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem'
            }}>
              <Check size={32} color="#16a34a" />
            </div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: '0 0 0.5rem' }}>
              Password Reset!
            </h1>
            <p style={{ fontSize: '0.9rem', color: '#6b7280', marginBottom: '2rem' }}>
              Your password has been reset successfully.
            </p>
            <Link
              to="/secure-login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '1rem 2rem',
                background: '#0073ea',
                color: '#fff',
                borderRadius: '10px',
                textDecoration: 'none',
                fontWeight: 600
              }}
            >
              Sign In
            </Link>
          </div>
        )}

        {/* Email Step */}
        {step === 'email' && (
          <>
            <Link
              to="/secure-login"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6b7280',
                textDecoration: 'none',
                fontSize: '0.9rem',
                marginBottom: '1.5rem'
              }}
            >
              <ArrowLeft size={18} />
              Back to Login
            </Link>

            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: 'linear-gradient(135deg, #0073ea 0%, #0052a3 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <KeyRound size={32} color="#fff" />
              </div>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                Forgot Password?
              </h1>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Enter your email and we'll send you a reset code
              </p>
            </div>

            <form onSubmit={handleRequestCode}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Email Address
                </label>
                <div style={{ position: 'relative' }}>
                  <Mail size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    required
                    data-testid="forgot-email"
                    style={{
                      width: '100%',
                      padding: '0.875rem 0.875rem 0.875rem 2.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="send-code-btn"
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: '#0073ea',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: loading ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {loading ? <Loader2 size={20} className="animate-spin" /> : null}
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </form>
          </>
        )}

        {/* Code Verification Step */}
        {step === 'code' && (
          <>
            <button
              onClick={() => setStep('email')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6b7280',
                background: 'none',
                border: 'none',
                fontSize: '0.9rem',
                marginBottom: '1.5rem',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <ArrowLeft size={18} />
              Change Email
            </button>

            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                Enter Code
              </h1>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                We sent a 6-digit code to<br />
                <strong>{email}</strong>
              </p>
            </div>

            <form onSubmit={handleVerifyCode}>
              <div style={{ marginBottom: '1.5rem' }}>
                <input
                  type="text"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  data-testid="reset-code-input"
                  style={{
                    width: '100%',
                    padding: '1rem',
                    fontSize: '2rem',
                    fontWeight: 700,
                    textAlign: 'center',
                    letterSpacing: '0.5rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '12px',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <button
                type="submit"
                disabled={code.length !== 6}
                data-testid="verify-code-btn"
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: code.length === 6 ? '#0073ea' : '#e5e7eb',
                  color: code.length === 6 ? '#fff' : '#9ca3af',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: code.length === 6 ? 'pointer' : 'not-allowed'
                }}
              >
                Continue
              </button>
            </form>

            <p style={{ textAlign: 'center', marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
              Code expires in 15 minutes
            </p>
          </>
        )}

        {/* New Password Step */}
        {step === 'newPassword' && (
          <>
            <button
              onClick={() => setStep('code')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#6b7280',
                background: 'none',
                border: 'none',
                fontSize: '0.9rem',
                marginBottom: '1.5rem',
                cursor: 'pointer',
                padding: 0
              }}
            >
              <ArrowLeft size={18} />
              Back
            </button>

            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1f2937', margin: 0 }}>
                Create New Password
              </h1>
              <p style={{ fontSize: '0.9rem', color: '#6b7280', marginTop: '0.5rem' }}>
                Enter your new password below
              </p>
            </div>

            <form onSubmit={handleResetPassword}>
              {/* New Password */}
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  New Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password"
                    required
                    data-testid="new-password"
                    style={{
                      width: '100%',
                      padding: '0.875rem 0.875rem 0.875rem 2.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
                
                {/* Password Requirements */}
                {newPassword.length > 0 && (
                  <div style={{ 
                    marginTop: '0.75rem', 
                    padding: '0.75rem', 
                    background: '#f9fafb', 
                    borderRadius: '8px',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '0.5rem'
                  }}>
                    <PasswordCheck passed={passwordChecks.length} label="8+ characters" />
                    <PasswordCheck passed={passwordChecks.uppercase} label="Uppercase letter" />
                    <PasswordCheck passed={passwordChecks.number} label="Number" />
                    <PasswordCheck passed={passwordChecks.special} label="Special character" />
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, color: '#374151', marginBottom: '0.5rem' }}>
                  Confirm Password
                </label>
                <div style={{ position: 'relative' }}>
                  <Lock size={18} color="#9ca3af" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    required
                    data-testid="confirm-new-password"
                    style={{
                      width: '100%',
                      padding: '0.875rem 2.75rem 0.875rem 2.75rem',
                      border: `1px solid ${confirmPassword.length > 0 ? (passwordsMatch ? '#16a34a' : '#ef4444') : '#e5e7eb'}`,
                      borderRadius: '10px',
                      fontSize: '0.95rem',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                  {confirmPassword.length > 0 && (
                    <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }}>
                      {passwordsMatch ? <Check size={18} color="#16a34a" /> : <X size={18} color="#ef4444" />}
                    </div>
                  )}
                </div>
              </div>

              <button
                type="submit"
                disabled={loading || !isPasswordValid || !passwordsMatch}
                data-testid="reset-password-btn"
                style={{
                  width: '100%',
                  padding: '1rem',
                  background: isPasswordValid && passwordsMatch ? '#0073ea' : '#e5e7eb',
                  color: isPasswordValid && passwordsMatch ? '#fff' : '#9ca3af',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: isPasswordValid && passwordsMatch && !loading ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {loading ? <Loader2 size={20} className="animate-spin" /> : null}
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
